import {
  ElementRef,
  Fragment,
  PropsWithChildren,
  ReactNode,
  Suspense,
  useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react-markdown/lib/react-markdown';
import styled, { css } from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import type { RewardBox } from '@sorare/trois/src/components/ClaimRewardBoxes/type';
import { Button } from 'atoms/buttons/Button';
import { FullScreen } from 'atoms/layout/FullScreen';
import { Horizontal } from 'atoms/layout/flex';
import { LoadingIndicator } from 'atoms/loader/LoadingIndicator';
import { No3dSupport } from 'components/No3dSupport';
import { Dialog } from 'components/dialog';
import { CloseButton } from 'components/dialog/CloseButton';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import { glossary } from 'lib/glossary';
import { lazy } from 'lib/retry';

import { messages } from './utils';

const ClaimRewardBoxes = lazy(
  async () => import('@sorare/trois/src/components/ClaimRewardBoxes'),
  'ClaimRewardBoxes'
);

const centered = css`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
  margin-top: var(--current-stack-height);
`;
const CtaWrapper = styled.div`
  bottom: var(--triple-unit);
  ${centered}
`;
const LabelWrapper = styled(Horizontal)`
  bottom: calc(10 * var(--unit));
  ${centered}
`;

type RewardBoxDialogProps = {
  boxes: RewardBox[];
  preloaders: ReactElement[];
  fallbacks: ReactNode;
  open?: boolean;
  onClose: () => void;
  disableOnClose?: boolean;
  disableContinue?: boolean;
};
export const RewardBoxDialog = ({
  boxes,
  preloaders,
  fallbacks,
  open,
  onClose,
  disableOnClose,
  disableContinue,
  children,
}: PropsWithChildren<RewardBoxDialogProps>) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const claimRewardBoxesRef = useRef<ElementRef<typeof ClaimRewardBoxes>>(null);

  if (boxes.length === 0) {
    return null;
  }

  return (
    <>
      {open && (
        <Dialog fullScreen open={open} hideHeader>
          <FullScreen>
            <Suspense fallback={<LoadingIndicator fullScreen />}>
              <ClaimRewardBoxes
                boxes={boxes}
                ref={claimRewardBoxesRef}
                fallback={<No3dSupport>{fallbacks}</No3dSupport>}
              />
            </Suspense>
            {!boxes.some(({ opened }) => opened) && isTabletAndAbove && (
              <LabelWrapper>
                <Button
                  color="secondary"
                  size="small"
                  onClick={() => {
                    claimRewardBoxesRef.current?.openCurrent();
                  }}
                >
                  <FormattedMessage {...messages.clickToOpenIt} />
                </Button>
              </LabelWrapper>
            )}
            {!disableContinue && boxes.every(({ opened }) => opened) && (
              <CtaWrapper>
                <Button color="secondary" onClick={onClose} size="medium">
                  <FormattedMessage {...glossary.continue} />
                </Button>
              </CtaWrapper>
            )}
            {!disableOnClose && (
              <CloseButtonWrapper>
                <CloseButton onClose={onClose} />
              </CloseButtonWrapper>
            )}
            {children}
          </FullScreen>
        </Dialog>
      )}
      {preloaders.map((preloader, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>{preloader}</Fragment>
      ))}
    </>
  );
};
